import { Helmet } from 'react-helmet-async';
import './landing-variants.css';

/* V11 — "Deep Sea Zenith"
   Immersive navy-and-silver palette. Layered glassmorphism, deep shadows,
   liquid-like transitions, oceanic depth.
   Noto Serif Display (display) + Plus Jakarta Sans (body). */

const navy = '#0b1628';
const deepNavy = '#060e1c';
const silver = '#c8d0dc';
const accent = '#4ea8de';
const lightSilver = '#e2e8f0';

const features = [
  { icon: '◇', title: 'Agenda intelligent', desc: 'Créneaux optimisés automatiquement, planification sans faille.' },
  { icon: '◈', title: 'Dossiers patients', desc: 'Historique complet, consultation instantanée, zéro friction.' },
  { icon: '◆', title: 'Facturation fluide', desc: 'Devis et factures depuis un flux continu, sans interruption.' },
  { icon: '⬡', title: 'Multi-cabinet', desc: 'Plusieurs sites, un seul tableau de bord immersif.' },
];

export default function LandingV11() {
  const display = "'Noto Serif Display', serif";
  const body = "'Plus Jakarta Sans', sans-serif";

  return (
    <div className="doctif-page" style={{ fontFamily: body, background: navy, color: silver, overflow: 'hidden' }}>
      <Helmet><title>doctif — Deep Sea Zenith</title></Helmet>

      {/* Oceanic gradient background layers */}
      <div aria-hidden style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: `
          radial-gradient(ellipse 80% 50% at 20% 80%, rgba(78,168,222,0.08) 0%, transparent 70%),
          radial-gradient(ellipse 60% 40% at 80% 20%, rgba(78,168,222,0.05) 0%, transparent 60%),
          radial-gradient(ellipse 100% 60% at 50% 100%, rgba(6,14,28,0.9) 0%, transparent 50%),
          linear-gradient(180deg, ${navy} 0%, ${deepNavy} 100%)
        `,
      }} />

      {/* Subtle moving light caustic */}
      <div aria-hidden style={{
        position: 'fixed', top: '-30%', left: '-10%', width: '120%', height: '60%',
        background: 'radial-gradient(ellipse at center, rgba(78,168,222,0.04) 0%, transparent 70%)',
        animation: 'doctifFloat 12s ease-in-out infinite', zIndex: 0, pointerEvents: 'none',
      }} />

      {/* Silver accent line */}
      <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${accent}44, transparent)`, position: 'relative', zIndex: 2 }} />

      {/* ── Header ── */}
      <header className="doctif-shell" style={{ padding: '28px 0 20px', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontFamily: display, fontSize: 34, fontWeight: 700, color: lightSilver, letterSpacing: '-0.02em' }}>
            doctif
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <nav className="doctif-chip-row">
              {Array.from({ length: 15 }, (_, i) => (
                <a key={i} className="doctif-chip" href={`/${i + 1}/`} style={{
                  borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 600,
                  background: i === 10 ? `${accent}22` : 'rgba(255,255,255,0.04)',
                  color: i === 10 ? accent : 'rgba(200,208,220,0.5)',
                  border: `1px solid ${i === 10 ? accent + '44' : 'rgba(255,255,255,0.06)'}`,
                  backdropFilter: 'blur(8px)',
                }}>{String(i + 1).padStart(2, '0')}</a>
              ))}
            </nav>
            <a href="/accounts/login/" style={{
              color: accent, textDecoration: 'none', fontWeight: 600, fontSize: 13,
              padding: '8px 20px', border: `1px solid ${accent}33`,
              borderRadius: 24, backdropFilter: 'blur(12px)', background: 'rgba(78,168,222,0.06)',
              transition: 'all 0.4s',
            }}>Connexion</a>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 60, paddingBottom: 80 }}>
        <p className="doctif-kicker doctif-fade-up" style={{ color: accent, marginBottom: 24, letterSpacing: '0.3em' }}>
          Immersion clinique
        </p>
        <h1 className="doctif-fade-up doctif-delay-1" style={{
          fontFamily: display, fontWeight: 700,
          fontSize: 'clamp(2.6rem, 6.5vw, 5.8rem)', lineHeight: 0.95,
          maxWidth: 800, marginBottom: 28, letterSpacing: '-0.025em', color: lightSilver,
        }}>
          Au plus profond<br />
          <span style={{ color: accent, fontStyle: 'italic' }}>de l'excellence</span>
        </h1>
        <p className="doctif-fade-up doctif-delay-2" style={{
          fontSize: 17, lineHeight: 1.8, color: 'rgba(200,208,220,0.6)', maxWidth: 520, marginBottom: 40,
        }}>
          doctif plonge au cœur de la gestion clinique. Une interface immersive qui révèle chaque
          détail avec la clarté d'eaux profondes et cristallines.
        </p>
        <div className="doctif-fade-up doctif-delay-3" style={{ display: 'flex', gap: 16 }}>
          <a href="/accounts/login/" style={{
            display: 'inline-block', padding: '14px 36px', background: accent,
            color: deepNavy, fontWeight: 700, fontSize: 14, borderRadius: 28,
            textDecoration: 'none', boxShadow: `0 8px 32px ${accent}33`,
            transition: 'all 0.4s',
          }}>Plonger</a>
          <a href="#modules" style={{
            display: 'inline-block', padding: '14px 36px',
            border: `1px solid rgba(200,208,220,0.15)`, borderRadius: 28,
            color: silver, fontWeight: 600, fontSize: 14, textDecoration: 'none',
            backdropFilter: 'blur(12px)', background: 'rgba(255,255,255,0.03)',
            transition: 'all 0.4s',
          }}>Explorer</a>
        </div>
      </section>

      {/* ── Stats — Glass bar ── */}
      <div style={{ position: 'relative', zIndex: 5 }}>
        <div className="doctif-shell">
          <div className="doctif-fade-up doctif-delay-4" style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2,
            background: 'rgba(255,255,255,0.03)', borderRadius: 20, overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
          }}>
            {[
              { v: '240+', l: 'Cabinets connectés' },
              { v: '61k', l: 'Rendez-vous / mois' },
              { v: '34%', l: 'Temps économisé' },
            ].map((s, i) => (
              <div key={i} style={{
                padding: '28px 24px', textAlign: 'center',
                borderRight: i < 2 ? '1px solid rgba(255,255,255,0.06)' : 'none',
              }}>
                <div style={{ fontFamily: display, fontSize: 36, fontWeight: 700, color: lightSilver, letterSpacing: '-0.03em' }}>{s.v}</div>
                <div style={{ fontSize: 12, color: 'rgba(200,208,220,0.4)', marginTop: 4, letterSpacing: '0.05em' }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Features — Glassmorphism cards ── */}
      <section id="modules" className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 80, paddingBottom: 80 }}>
        <p className="doctif-kicker" style={{ color: accent, marginBottom: 12, letterSpacing: '0.3em' }}>Modules</p>
        <h2 style={{ fontFamily: display, fontWeight: 700, fontSize: 'clamp(1.6rem, 3vw, 2.6rem)', color: lightSilver, marginBottom: 48, letterSpacing: '-0.02em' }}>
          Quatre piliers
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
          {features.map((f, i) => (
            <div key={i} className="doctif-fade-up" style={{
              padding: '36px 32px', borderRadius: 18,
              background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
              animationDelay: `${i * 120}ms`, transition: 'transform 0.4s, box-shadow 0.4s',
            }}>
              <div style={{ fontSize: 28, marginBottom: 16, color: accent, opacity: 0.7 }}>{f.icon}</div>
              <h3 style={{ fontFamily: display, fontWeight: 700, fontSize: 20, color: lightSilver, marginBottom: 10, letterSpacing: '-0.01em' }}>{f.title}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: 'rgba(200,208,220,0.5)' }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Dashboard Preview — Abyss Panel ── */}
      <section style={{ position: 'relative', zIndex: 5, paddingBottom: 80 }}>
        <div className="doctif-shell">
          <div style={{
            background: 'rgba(0,0,0,0.4)', borderRadius: 20, padding: 32,
            border: '1px solid rgba(255,255,255,0.05)',
            boxShadow: '0 30px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.03)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <span style={{ fontFamily: display, fontWeight: 700, fontSize: 18, color: lightSilver }}>Tableau de bord</span>
              <span style={{ fontSize: 11, color: 'rgba(200,208,220,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Temps réel</span>
            </div>
            {/* Activity wave */}
            <div style={{ display: 'flex', alignItems: 'end', gap: 4, height: 80, marginBottom: 24 }}>
              {[30, 45, 60, 40, 75, 90, 55, 70, 85, 50, 65, 80, 45, 60, 35, 70, 55, 90, 40, 75].map((h, i) => (
                <div key={i} style={{
                  flex: 1, height: `${h}%`, borderRadius: '4px 4px 0 0',
                  background: `linear-gradient(180deg, ${accent}88, ${accent}22)`,
                  opacity: 0.6 + (h / 300),
                }} />
              ))}
            </div>
            {/* Entries */}
            {['Dr. Martin — Contrôle annuel — 09:00', 'Dr. Leroy — Pose implant — 10:30', 'Dr. Fabre — Détartrage — 14:00'].map((e, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', padding: '14px 0',
                borderTop: '1px solid rgba(255,255,255,0.04)', fontSize: 13,
                color: 'rgba(200,208,220,0.5)',
              }}>
                <span>{e}</span>
                <span style={{
                  padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                  background: i === 1 ? 'rgba(255,180,50,0.12)' : `${accent}15`,
                  color: i === 1 ? '#ffb432' : accent,
                }}>{i === 1 ? 'En cours' : 'Confirmé'}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingBottom: 80 }}>
        <p className="doctif-kicker" style={{ color: accent, marginBottom: 12, letterSpacing: '0.3em' }}>Témoignages</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginTop: 32 }}>
          {[
            { q: 'L\'immersion dans les données est totale. On ne revient plus en arrière.', a: 'Dr. Elise Moreau', r: 'Paris' },
            { q: 'Chaque écran respire le premium. Mes patients le remarquent.', a: 'Dr. Julien Fabre', r: 'Lyon' },
            { q: 'La profondeur fonctionnelle est impressionnante, l\'interface reste sereine.', a: 'Dr. Nadia Hamdi', r: 'Marseille' },
          ].map((t, i) => (
            <div key={i} style={{
              padding: '28px 24px', borderRadius: 16,
              background: 'rgba(255,255,255,0.02)', backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: 'rgba(200,208,220,0.6)', marginBottom: 20, fontStyle: 'italic' }}>« {t.q} »</p>
              <p style={{ fontFamily: display, fontWeight: 700, fontSize: 14, color: lightSilver }}>{t.a}</p>
              <p style={{ fontSize: 12, color: 'rgba(200,208,220,0.3)' }}>{t.r}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust bar ── */}
      <div style={{ position: 'relative', zIndex: 5, borderTop: '1px solid rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="doctif-shell" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, padding: '20px 0' }}>
          {[{ k: 'Chiffrement', v: 'AES-256' }, { k: 'Hébergement', v: 'HDS France' }, { k: 'Uptime', v: '99.9%' }, { k: 'Support', v: '< 2h' }].map((t, i) => (
            <div key={i} style={{ textAlign: 'center', borderRight: i < 3 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
              <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(200,208,220,0.3)', marginBottom: 4 }}>{t.k}</div>
              <div style={{ fontFamily: display, fontWeight: 700, fontSize: 16, color: lightSilver }}>{t.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── CTA ── */}
      <section className="doctif-shell" style={{ position: 'relative', zIndex: 5, paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
        <h2 style={{ fontFamily: display, fontWeight: 700, fontSize: 'clamp(2rem, 4.5vw, 3.4rem)', color: lightSilver, marginBottom: 28, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
          Prêt à plonger<br /><span style={{ color: accent, fontStyle: 'italic' }}>en profondeur ?</span>
        </h2>
        <a href="/accounts/login/" style={{
          display: 'inline-block', padding: '16px 48px', background: accent,
          color: deepNavy, fontWeight: 700, fontSize: 15, borderRadius: 28,
          textDecoration: 'none', boxShadow: `0 12px 40px ${accent}33`,
          transition: 'all 0.4s',
        }}>Commencer l'immersion</a>
      </section>

      {/* ── Footer ── */}
      <footer style={{ position: 'relative', zIndex: 5, borderTop: '1px solid rgba(255,255,255,0.05)', padding: '20px 0' }}>
        <div className="doctif-shell" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: display, fontSize: 14, color: 'rgba(200,208,220,0.4)' }}>doctif — Direction Deep Sea Zenith</span>
          <span style={{ fontSize: 12, color: 'rgba(200,208,220,0.25)' }}>v11.0 — {new Date().getFullYear()}</span>
        </div>
      </footer>

      <div style={{ height: 1, background: `linear-gradient(90deg, transparent, ${accent}33, transparent)`, position: 'relative', zIndex: 2 }} />
    </div>
  );
}
